import { Box, Divider, Stack, styled, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AppRegistration,
  Dashboard,
  Group,
  ChevronRight,
  Work,
  PermContactCalendar,
  Storefront,
  BookOnline,
  WhatsApp,
  Phone,
  Flight,
  Hotel,
  BusinessCenter,
} from '@mui/icons-material';
import { Can } from '@casl/react';

import { useSidebar } from '../../contexts/SidebarContext/hook';
import { DashboardButtonLink } from '../DashboardButtonLink';
import { Drawer, OpenIconButton } from './styles';

import { routes } from '@frontend/router/routes';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import {
  FLIGHT_SEARCH_UNDER_HEADER_HEIGHT,
  HOTEL_SEARCH_UNDER_HEADER_HEIGHT,
} from '@frontend/features/common/constants/layout';

interface StyledBoxProps {
  isOpen: boolean;
  paddingTop: number;
  isMobileCompressed: boolean;
}

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'hasNavSearch' && prop !== 'isMobileCompressed',
})<StyledBoxProps>(({ theme, isOpen, paddingTop, isMobileCompressed }) => ({
  paddingTop: paddingTop ? paddingTop : 0,
  marginTop: isMobileCompressed ? theme.spacing(5) : 0,
  display: 'flex',
  height: 40,
  transition: '.4s',
  alignItems: 'center',
  justifyContent: isOpen ? 'flex-end' : 'center',
}));

const determineStyledBoxHeight = (hasHotels: boolean, hasFlights: boolean) => {
  let result = 0;
  if (hasHotels) {
    result = HOTEL_SEARCH_UNDER_HEADER_HEIGHT;
  } else if (hasFlights) {
    result = FLIGHT_SEARCH_UNDER_HEADER_HEIGHT;
  }
  return result;
};

export const DashboardSidebar = () => {
  const theme = useTheme();
  const { ability } = useAuth();
  const { isOpen, handleSidebarOpening, hasNavHotelSearch, hasNavFlightsSearch } = useSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const postaBrakepoint = useMediaQuery('(min-width:758px)');

  const handleButtonLinkClick = (link: string) => () => {
    navigate(link, { replace: true });
  };

  return (
    ability && (
      <Drawer
        sx={{
          zIndex: 2,
        }}
        variant="permanent"
        open={isOpen}
      >
        <>
          <StyledBox  isOpen={isOpen} paddingTop={determineStyledBoxHeight(hasNavHotelSearch, hasNavFlightsSearch)} isMobileCompressed={!postaBrakepoint}>
            <OpenIconButton
              size="small"
              aria-label={t(`common.actions.${isOpen ? 'open' : 'close'}`)}
              onClick={handleSidebarOpening}
              isSidebarOpen={isOpen}
            >
              <ChevronRight />
            </OpenIconButton>
          </StyledBox>
          {isOpen ? (
            <Box display="flex" gap={1} alignItems="center" padding={(theme) => theme.spacing(1, 0)}>
              <Divider aria-hidden="true" sx={{ width: '24px' }} />
              <Typography variant="body2" color={theme.palette.disabled.main} textTransform="uppercase">
                {t('common.general')}
              </Typography>
            </Box>
          ) : (
            <Divider
              sx={{
                width: 48,
                margin: (theme) => theme.spacing(2.25, 0),
              }}
              aria-hidden="true"
              variant="middle"
            />
          )}

          <Stack gap={1}>
            <Can I="view" a="Dashboard" ability={ability}>
              <DashboardButtonLink
                icon={<Dashboard />}
                isActive={location.pathname === routes.dashboard.index}
                label={t('common.nav.dashboard')}
                onClick={handleButtonLinkClick(routes.dashboard.index)}
              />
            </Can>
            <Can I="view" a="SaleOrders" ability={ability}>
              <DashboardButtonLink
                icon={<BookOnline />}
                isActive={location.pathname === routes.dashboard.saleOrder.root}
                label={t('common.nav.saleOrders')}
                onClick={handleButtonLinkClick(routes.dashboard.saleOrder.root)}
              />
            </Can>
            <Can I="view" a="Calls" ability={ability}>
              <DashboardButtonLink
                icon={<WhatsApp />}
                isActive={location.pathname.includes(routes.dashboard.whatsapp)}
                label={t('common.nav.whatsapp')}
                onClick={handleButtonLinkClick(routes.dashboard.whatsapp)}
              />
            </Can>
            <Can I="view" a="Calls" ability={ability}>
              <DashboardButtonLink
                icon={<Phone />}
                isActive={location.pathname.includes(routes.dashboard.calls)}
                label={t('common.nav.calls')}
                onClick={handleButtonLinkClick(routes.dashboard.calls)}
              />
            </Can>
            <Can I="view" a="Cases" ability={ability}>
              <DashboardButtonLink
                icon={<Work />}
                isActive={location.pathname.includes(routes.dashboard.cases)}
                label={t('common.nav.cases')}
                onClick={handleButtonLinkClick(routes.dashboard.cases)}
              />
            </Can>
            <Can I="view" a="Clients" ability={ability}>
              <DashboardButtonLink
                icon={<PermContactCalendar />}
                isActive={location.pathname.includes(routes.dashboard.clients)}
                label={t('common.nav.clients')}
                onClick={handleButtonLinkClick(routes.dashboard.clients)}
              />
            </Can>
            <Can I="view" a="Agencies" ability={ability}>
              <DashboardButtonLink
                icon={<Storefront />}
                isActive={location.pathname.includes(routes.dashboard.agencies)}
                label={t('common.nav.agencies')}
                onClick={handleButtonLinkClick(routes.dashboard.agencies)}
              />
            </Can>
            <Can I="manage" a="Users" ability={ability}>
              <DashboardButtonLink
                icon={<Group />}
                isActive={location.pathname.includes(routes.dashboard.users)}
                label={t('common.nav.users')}
                onClick={handleButtonLinkClick(routes.dashboard.users)}
              />
            </Can>
            <Can I="view" a="ContentManager" ability={ability}>
              <DashboardButtonLink
                icon={<AppRegistration />}
                isActive={location.pathname.includes(routes.dashboard.content.hotel.list)}
                label={t('hotels.contentManager.title')}
                onClick={handleButtonLinkClick(routes.dashboard.content.hotel.list)}
              />
            </Can>
            <Can I="view" a="ContentManager" ability={ability}>
              <DashboardButtonLink
                icon={<Hotel />}
                isActive={location.pathname.includes(routes.dashboard.hotelContentManagement.root)}
                label={t('common.nav.hotelContentManagement')}
                onClick={handleButtonLinkClick(routes.dashboard.hotelContentManagement.root)}
              />
            </Can>
            <Can I="manage" a="FlightIntegrations" ability={ability}>
              <DashboardButtonLink
                icon={<Flight />}
                isActive={location.pathname.includes(routes.dashboard.flightIntegrations.list)}
                label={t('common.nav.flightIntegrations')}
                onClick={handleButtonLinkClick(routes.dashboard.flightIntegrations.list)}
              />
            </Can>
            <Can I="manage" a="HotelIntegrations" ability={ability}>
              <DashboardButtonLink
                icon={<BusinessCenter />}
                isActive={location.pathname.includes(routes.dashboard.hotelIntegrations.list)}
                label={t('common.nav.hotelIntegrations')}
                onClick={handleButtonLinkClick(routes.dashboard.hotelIntegrations.list)}
              />
            </Can>
          </Stack>
        </>
      </Drawer>
    )
  );
};
